import React from 'react'
import PropTypes from 'prop-types'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import { Box, Popper, ClickAwayListener } from '@material-ui/core'
import Toggle from 'react-toggle'
import cn from 'classnames'

import { Card, CardHeading, CardContent } from '@components/layout/Card'
import { Projection } from '@components/Projection'
import { Badge } from '@components/Badge'
import { Tooltip } from '@components/Tooltip'
import { buildProjectionDatasets } from './datasets'
import { track } from '@utils/tracking'

import { TimeValueToggle } from '@components/TimeValueToggle'
import { TimeValue, TimeValuePropType } from '@hooks/useTimeValue'
import { resolveJointInflation, describeJointInflation, formatInflation } from './datasets/inflation'
import { Hider } from '@components/HOCs/Hider'

export const ChartTypes = {
  income: "income",
  capital: "capital"
}

export const ChartView = {
  individual: "individual",
  joint: "joint"
}

export const JointForecast = (props) => {
  const [state, setState] = React.useState({
    chartType: ChartTypes.capital,
    chartView: ChartView.individual,
    retirementAge: props.retirementAge,
    showTotalExpense: false,
    showGrossIncome: false,
  })

  const setChartType = (chartType) => (evt) => {
    evt.preventDefault()
    track('Forecast', `Set chart type to ${chartType}`)

    setState(previousState => ({
      ...previousState,
      chartType: chartType
    }))
  }

  const setChartView = (chartView) => (evt) => {
    evt.preventDefault()
    track('Joint Forecast', `Set chart view to ${chartView}`)

    setState(previousState => ({
      ...previousState,
      chartView: chartView
    }))
  }

  const setShowGrossIncome = (value) => {
    track('Forecast', `Set show gross income to ${value}`)
    setState(previousState => ({
      ...previousState,
      showGrossIncome: value
    }))
  }

  const setShowTotalExpense = (value) => {
    track('Forecast', `Set show total expense to ${value}`)
    setState(previousState => ({
      ...previousState,
      showTotalExpense: value
    }))
  }

  const resetToComparativeView = () => {
    track('Forecast', `Reset to comparative view`)
    setState(previousState => ({
      ...previousState,
      showGrossIncome: false,
      showTotalExpense: false
    }))
  }

  // This is a very expensive call that can potentially
  // be memoized if we have some perf issues here
  const datasets = buildProjectionDatasets({
    forecast: props.forecast,
    participantsQueries: props.participantsQueries,
    chartType: state.chartType,
    chartView: state.chartView,
    timeValue: props.timeValue,
    retirementAge: props.retirementAge,
    colors: props.colors,
    showTotalExpense: state.showTotalExpense,
    showGrossIncome: state.showGrossIncome,
  })

  return (
    <Card className='ui-card ui-card--projections-chart'>
      <CardHeading className={`${state.chartType === ChartTypes.capital ? 'items-center sm:items-start' : ''}`}>
        <ForecastHeader
          clientsJointPronoun={props.clientsJointPronoun}
          chartType={state.chartType}
          setChartType={setChartType}
          setChartView={setChartView}
          chartView={state.chartView}
          showTotalExpense={state.showTotalExpense}
          showGrossIncome={state.showGrossIncome}
          setShowTotalExpense={setShowTotalExpense}
          setShowGrossIncome={setShowGrossIncome}
          resetToComparativeView={resetToComparativeView}
        />
      </CardHeading>
      <CardContent>
        <Projection
          stacked
          datasets={datasets}
          colors={props.colors}
          chartType={state.chartType}
          timeValue={props.timeValue}
        />
        <InflationNote
          participantsQueries={props.participantsQueries}
          chartView={state.chartView}
          timeValue={props.timeValue}
        />
      <div className='flex sm:hidden justify-center'>
        <TimeValueToggle
          setTimeValue={props.setTimeValue}
          timeValue={props.timeValue}
        />
      </div>
      </CardContent>
    </Card>
  )
}

/**
 * Names the inflation rate the chart in front of you is discounting by.
 *
 * Two plans can carry two rates, and the joint line can only use one. Which one
 * was picked, and why, is not something a reader can recover from the curve —
 * so it is said in words rather than left to be worked out from a number that
 * looks slightly wrong.
 *
 * Nothing is shown in future value, where no discounting happens at all.
 */
const InflationNote = ({ participantsQueries, chartView, timeValue }) => {
  if (timeValue !== TimeValue.present) return null

  const resolution = resolveJointInflation(participantsQueries)

  // The stacked view draws a line per client, each already discounted at that
  // client's own rate. There is nothing to resolve; there is still something
  // to say, because two lines discounted differently sit on one axis.
  const text = chartView === ChartView.joint
    ? describeJointInflation(resolution)
    : `Present value, each plan at its own inflation — ` +
      `${resolution.participants.map((p) => `${p.name} ${formatInflation(p.rate)}`).join(', ')}.`

  if (resolution.participants.length === 0) return null

  return (
    <small className='block mt-2 text-center text-gray-500' data-testid='joint-inflation-note'>
      {text}
    </small>
  )
}

const ForecastHeader = ({
  clientPronoun, chartType, setChartType, setChartView, chartView,
  showGrossIncome, showTotalExpense, setShowGrossIncome, setShowTotalExpense,
  resetToComparativeView
}) => {
  const { menuAnchorElement, toggleMenu, closeMenu, menuVisible, menuId } = useIncomeChartsMenu()
  return (
    <>
      <h5>{clientPronoun} {chartType === ChartTypes.income ? 'Income' : 'Capital'} Projection</h5>
      <Box className='flex justify-between items-center mt-2'>
        <div className='flex gap-4 flex-wrap items-center'>
          <div className='chart-actions'>
            <a className={chartType === ChartTypes.income ? '' : 'active'} onClick={setChartType(ChartTypes.capital)}>
              Capital Chart
            </a>
            <a className={chartType === ChartTypes.income ? 'active' : ''} onClick={setChartType(ChartTypes.income)}>
              Income Chart
            </a>
          </div>
          <div className='chart-actions'>
            <a className={cn({
                ['active']: chartView === ChartView.individual,
                ['!text-[#c0c0c0] bg-[#efefef] cursor-not-allowed pointer-events-none']: chartType === ChartTypes.income && (showGrossIncome || showTotalExpense)
              })}
              onClick={setChartView(ChartView.individual)}>
              Stacked
            </a>
            <a className={chartView === ChartView.joint ? 'active' : ''} onClick={setChartView(ChartView.joint)}>
              Joint
            </a>
          </div>
        </div>
        { chartType === ChartTypes.income &&
        <Box className='flex items-center gap-4'>
          { (showGrossIncome || showTotalExpense) &&
            <small className='hidden sm:block'>
              <a onClick={resetToComparativeView}>
                Reset view
              </a>
            </small>
          }
          <MoreVertIcon
            aria-describedby={menuId}
            onClick={toggleMenu}
            style={{ cursor: 'pointer' }} />
          <Popper
            id={menuId}
            open={menuVisible}
            anchorEl={menuAnchorElement}
            placement='bottom-end'
          >
            <ClickAwayListener onClickAway={closeMenu}>
              <Box>
                <Hider show={menuVisible} withTransition
                  render={() => (
                    <Card>
                      <CardHeading>
                        <Box className={'flex justify-between gap-8'}>
                          <p>View joint total expense</p>
                          <Toggle
                            checked={showTotalExpense}
                            onChange={(e) => setShowTotalExpense(e.target.checked)}
                            icons={false}
                            className='app-toggle'
                          />
                        </Box>
                        <Box className={'flex justify-between gap-8'}>
                          <p>View joint gross income</p>
                          <Toggle
                            checked={showGrossIncome}
                            onChange={(e) => setShowGrossIncome(e.target.checked)}
                            icons={false}
                            className='app-toggle'
                          />
                        </Box>
                      </CardHeading>
                    </Card>
                  )}
                />
              </Box>
            </ClickAwayListener>
          </Popper>
        </Box>}
      </Box>
      { (showGrossIncome || showTotalExpense) &&
        <small className='sm:hidden text-right'>
          <a onClick={resetToComparativeView}>
            Reset view
          </a>
        </small>
      }
    </>
  )
}

const useIncomeChartsMenu = () => {
  const [menuAnchorElement, setMenuAnchorElement] = React.useState()
  const toggleMenu = (event) => {
    track('Forecast', `Toggle income breakdown menu`)
    setMenuAnchorElement(menuAnchorElement ? null : event.currentTarget)
  }
  const closeMenu = (event) => {
    track('Forecast', `Close income breakdown menu`)
    event && event.preventDefault()
    setMenuAnchorElement(null)
  }
  const menuVisible = Boolean(menuAnchorElement)
  const menuId = open ? 'plan-actions-popover-menu' : undefined

  return {
    menuAnchorElement,
    toggleMenu,
    closeMenu,
    menuVisible,
    menuId
  }
}

JointForecast.propTypes = {
  /* Global config */
  colors: PropTypes.object,
  clientsJointPronoun: PropTypes.string,
  assumptions: PropTypes.object,
  usePresentValue: PropTypes.bool,
  timeValue: TimeValuePropType.isRequired,
  setTimeValue: PropTypes.func,

  /* Component props */
  retirementAge: PropTypes.number,
  currentAge: PropTypes.number,
  forecast: PropTypes.exact({
    joint_forecast: PropTypes.arrayOf(PropTypes.object).isRequired,
    client_forecasts: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  participantsQueries: PropTypes.arrayOf(PropTypes.object).isRequired,
  showTicksInYear: PropTypes.bool,
  cashflowEvents: PropTypes.arrayOf(PropTypes.object),
}
